import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Provider, ProviderStatus } from '@app/core/models/provider.model';
import { Payer } from '@app/core/models/payer.model';
import { ProcedureCode } from '@app/core/models/procedure-code.model';
import { DxcMultiSelectOption } from '@dxc/ui-components/select';
import { AppConstants } from '@app/core/constants/app.constants';
import { DxcDropdownOption, DxcDropdownOptionValue } from '@dxc/ui-components/dropdown';

import { DxcSingleSelectOption } from '@dxc/ui-components/select';
@Component({
  selector: 'app-new-eligibility-form',
  templateUrl: './new-eligibility-form.component.html',
  styleUrls: ['./new-eligibility-form.component.scss']
})
export class NewEligibilityFormComponent implements OnInit {
  @Input() eligform: FormGroup = null!;
  @Input() submitLoading = false;
  @Input() set providers(data: Provider[]) {
    const activeProviders = data.filter((x) => x.status === ProviderStatus.ACTIVE);
    this._providersOptions = activeProviders.map((x) => ({
      name: `${x.firstName} ${x.lastName} - ${x.npi}`,
      value: x.id
    }));

    this._providerTinMap = activeProviders.reduce((acc, provider) => {
      acc[provider.id] = provider.tin;
      return acc;
    }, {} as Record<number, string>);
  }
  @Input() set payers(data: Payer[]) {
    this._payersOptions = data.map((x) => ({
      option: x.name,
      value: x.id
    }));
  }
  @Input() set procedurescode(codes: ProcedureCode[]) {
    if (codes) this.procedureCodeOptions = codes.map((e) => ({ option: e.code, value: e.code }));
  }
  @Input() set tags(tags: string[]) {
    if (tags) this.tagsOptions = tags.map((e) => ({ option: e, value: e }));
  }
  @Input() isEditAppointment = false;
  @Output() onSubmit = new EventEmitter();
  @Output() onSubscriberSameAsPatient = new EventEmitter();
  @Output() onProviderSelect = new EventEmitter();
  @Output() onClearForm = new EventEmitter();

  _providersOptions: DxcDropdownOption[] = [];
  _payersOptions: DxcSingleSelectOption[] = [];
  _providerTinMap: Record<number, string> = {};
  today = new Date();
  minDate = AppConstants.minDate;
  procedureCodeOptions: DxcMultiSelectOption[] = [];
  tagsOptions: DxcMultiSelectOption[] = [];
  relationshipOptions = AppConstants.subscriberRelationShipList;
  selectedProviderTaxId = '';

  ngOnInit() {
    this.initFormValue();
    this.initFieldSubscriptions();
    if (this.eligform.value['provider']) {
      this.getTaxId([this.eligform.value['provider']]);
    }
  }

  private initFieldSubscriptions() {
    ['zipCode', 'subscriberZipCode'].forEach((field) => {
      this.eligform.get(field)?.valueChanges.subscribe((value) => {
        if (value) {
          const formatted = this.formatZipCode(value);
          if (formatted !== value) {
            this.eligform.patchValue({ [field]: formatted }, { emitEvent: false });
          }
        }
      });
    });
  }
  private initFormValue() {
    const zipCodeField = this.eligform.get('zipCode');
    const subscriberZipCodeField = this.eligform.get('subscriberZipCode');

    if (zipCodeField?.value) {
      this.eligform.patchValue(
        { zipCode: this.formatZipCode(zipCodeField?.value) },
        { emitEvent: false }
      );
    }

    if (subscriberZipCodeField?.value) {
      this.eligform.patchValue(
        { subscriberZipCode: this.formatZipCode(subscriberZipCodeField?.value) },
        { emitEvent: false }
      );
    }
  }

  private formatZipCode(value: string): string {
    const digits = value.replace(/\D/g, '');

    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }

    return digits;
  }

  onEligibilityFormSubmit() {
    this.onSubmit.emit();
  }

  setProviderDetails(provider: any) {
    this.onProviderSelect.emit(provider?.target.value);
  }

  getTaxId(event: DxcDropdownOptionValue[]) {
    if (!event || event.length === 0) {
      this.selectedProviderTaxId = '';
      return;
    }
    this.selectedProviderTaxId = this._providerTinMap[event[0] as number] || '';
  }

  toggleProviderUIType() {
    this.eligform.patchValue({
      provideUIType: this.eligform.value['provideUIType'] === 'SELECT' ? 'ENTER' : 'SELECT'
    });
    this.getTaxId([]);
  }

  clearForm() {
    this.selectedProviderTaxId = '';
    this.onClearForm.emit();
  }
}
